import { z } from 'zod'
import { type CartePaiement, carteValide } from './cartes'
import {
  CLASSES_ERREUR_COLLECTE,
  TYPES_DEFI,
  TYPES_DOCUMENT_PORTAIL,
  type CadenceConnexion,
  type ClasseErreurCollecte,
  type TypeDefi,
  type TypeDocumentPortail,
} from './domaine'
import { collecteParRobot, compteSensible } from './portails'

// Collecte locale : règles pures du collecteur qui tourne sur l'ordinateur du titulaire
// (docs/collecteur-local.md). L'ordinateur reçoit un besoin typé, jamais un texte libre ; il rend
// des documents et des comptes bornés, jamais un identifiant, un cookie ni une session. Ce qui
// vient d'un portail (références, dates, noms) est une donnée non fiable, revalidée ici.

// Références des documents

/**
 * Référence d'un document chez le fournisseur : un identifiant opaque, jamais une adresse.
 * L'adaptateur construit lui-même l'adresse depuis sa base déclarée ; une référence qui
 * ressemble à une adresse, un chemin absolu ou une remontée est refusée.
 */
export function referenceDocumentValide(ref: string): boolean {
  if (!/^[\p{L}\p{N}._:/-]{1,120}$/u.test(ref)) return false
  if (ref.startsWith('/') || ref.includes('//') || ref.includes(':')) return false
  return !ref.split('/').some((s) => s === '..' || s === '.')
}

const reference = z.string().refine(referenceDocumentValide, 'Référence de document illisible.')
const mois = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mois attendu : AAAA-MM.')

// Besoin d'une collecte

export const MAX_DOCUMENTS_COLLECTE = 50

/**
 * Ce qu'Admin PMVB cherche sur une connexion : des types de documents, une période facultative
 * (mois inclus), des références attendues en priorité. Les références déjà relevées ne sont pas
 * dans le besoin : le serveur les joint quand l'ordinateur prend la collecte.
 */
export const besoinCollecte = z
  .object({
    types: z
      .array(z.enum(TYPES_DOCUMENT_PORTAIL))
      .min(1)
      .max(TYPES_DOCUMENT_PORTAIL.length)
      .default(['facture']),
    periodeDebut: mois.optional(),
    periodeFin: mois.optional(),
    referencesAttendues: z.array(reference).min(1).max(MAX_DOCUMENTS_COLLECTE).optional(),
    maxDocuments: z.int().min(1).max(MAX_DOCUMENTS_COLLECTE).default(MAX_DOCUMENTS_COLLECTE),
  })
  .strict()
  .refine((b) => !b.periodeDebut || !b.periodeFin || b.periodeDebut <= b.periodeFin, {
    message: 'Période inversée.',
    path: ['periodeFin'],
  })
export type BesoinCollecte = z.infer<typeof besoinCollecte>

export const besoinParDefaut = (): BesoinCollecte => ({
  types: ['facture'],
  maxDocuments: MAX_DOCUMENTS_COLLECTE,
})

/**
 * Premier mois qu'un adaptateur relève (il ne déplie pas sa liste au-delà) : celui du besoin ;
 * sans période demandée, l'exercice en cours et le précédent, soit janvier de l'année passée (les
 * entités clôturent au 31 décembre).
 */
export function premierMoisReleve(
  besoin: Pick<BesoinCollecte, 'periodeDebut'>,
  aujourdhui: string,
): string {
  return besoin.periodeDebut ?? `${Number(aujourdhui.slice(0, 4)) - 1}-01`
}

// Documents listés par un adaptateur

export type DocumentDisponible = {
  /** Identifiant opaque chez le fournisseur : clé du dédoublonnage par connexion. */
  ref: string
  type?: TypeDocumentPortail
  /** AAAA-MM-JJ. */
  date?: string
  /** AAAA-MM : le mois que couvre le document. */
  periode?: string
  montantCts?: number
  /** Nom court lu sur le portail : une donnée, jamais une consigne. */
  nom?: string
  /** Texte écrit dans l'adaptateur (profil du compte…), lu par le rangement. */
  indication?: string
  /**
   * Carte qui a réglé le document (réseau, quatre derniers chiffres), lue sur le portail : elle
   * propose qui a payé (tableau des cartes, packages/core/src/cartes.ts).
   */
  carte?: CartePaiement
}

function jourValide(j: unknown): j is string {
  if (typeof j !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(j)) return false
  const d = new Date(`${j}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === j
}

function nomSur(n: unknown): string | undefined {
  if (typeof n !== 'string') return undefined
  const propre = n
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .trim()
    .slice(0, 120)
  return propre || undefined
}

/**
 * Ce que l'adaptateur a lu, revalidé : une référence illisible écarte le document, un champ
 * illisible est seulement oublié (on ne sait pas, plutôt qu'une valeur fausse). Une référence vue
 * deux fois ne compte qu'une fois, la première.
 */
export function normaliserDocuments(
  lus: readonly (Partial<DocumentDisponible> & { ref?: unknown })[],
): DocumentDisponible[] {
  const vus = new Set<string>()
  const sortie: DocumentDisponible[] = []
  for (const l of lus) {
    if (typeof l.ref !== 'string' || !referenceDocumentValide(l.ref) || vus.has(l.ref)) continue
    vus.add(l.ref)
    const d: DocumentDisponible = { ref: l.ref }
    if (l.type && (TYPES_DOCUMENT_PORTAIL as readonly string[]).includes(l.type)) d.type = l.type
    if (jourValide(l.date)) d.date = l.date
    if (typeof l.periode === 'string' && mois.safeParse(l.periode).success) d.periode = l.periode
    if (Number.isSafeInteger(l.montantCts)) d.montantCts = l.montantCts
    const nom = nomSur(l.nom)
    if (nom) d.nom = nom
    if (typeof l.indication === 'string' && l.indication.length <= 500) d.indication = l.indication
    const carte = carteValide(l.carte)
    if (carte) d.carte = carte
    sortie.push(d)
  }
  return sortie
}

export type ChoixDocuments = {
  choisis: DocumentDisponible[]
  ecartes: { connus: number; horsType: number; horsPeriode: number; auDela: number }
}

/**
 * Ce qu'il faut télécharger : ni ce qui est déjà relevé, ni un type que le besoin ne demande pas,
 * ni un mois hors de la période (la date ramenée au mois à défaut de période) ; un document dont
 * on ignore le type ou le mois est gardé. Les références attendues passent devant, puis l'ordre
 * du portail ; au-delà de la limite, la collecte suivante prendra le reste.
 */
export function choisirDocuments(
  besoin: BesoinCollecte,
  disponibles: readonly DocumentDisponible[],
  connues: Iterable<string>,
): ChoixDocuments {
  const connuesSet = new Set(connues)
  const ecartes = { connus: 0, horsType: 0, horsPeriode: 0, auDela: 0 }
  const candidats: DocumentDisponible[] = []
  for (const d of disponibles) {
    if (connuesSet.has(d.ref)) {
      ecartes.connus++
      continue
    }
    if (d.type && !besoin.types.includes(d.type)) {
      ecartes.horsType++
      continue
    }
    const m = d.periode ?? d.date?.slice(0, 7)
    if (
      m &&
      ((besoin.periodeDebut && m < besoin.periodeDebut) ||
        (besoin.periodeFin && m > besoin.periodeFin))
    ) {
      ecartes.horsPeriode++
      continue
    }
    candidats.push(d)
  }
  const attendues = besoin.referencesAttendues ?? []
  const rang = (d: DocumentDisponible) => {
    const i = attendues.indexOf(d.ref)
    return i < 0 ? attendues.length : i
  }
  const ordonnes = candidats
    .map((d, i) => ({ d, i }))
    .sort((a, b) => rang(a.d) - rang(b.d) || a.i - b.i)
    .map((x) => x.d)
  const choisis = ordonnes.slice(0, besoin.maxDocuments)
  ecartes.auDela = ordonnes.length - choisis.length
  return { choisis, ecartes }
}

// Classes d'erreur

const CLASSES_HUMAINES: readonly ClasseErreurCollecte[] = [
  'auth_requise',
  'mfa_requis',
  'captcha_requis',
  'consentement_requis',
  'session_expiree',
]

/** Le fournisseur demande quelqu'un : rien de cassé, le titulaire doit se connecter. */
export function estInterventionHumaine(classe: ClasseErreurCollecte): boolean {
  return CLASSES_HUMAINES.includes(classe)
}

/** Classe de l'arrêt d'une collecte sur un défi. */
export function classeDuDefi(type: TypeDefi): ClasseErreurCollecte {
  switch (type) {
    case 'otp_sms':
    case 'otp_email':
    case 'totp':
    case 'push':
      return 'mfa_requis'
    case 'captcha':
      return 'captcha_requis'
    case 'consentement':
      return 'consentement_requis'
    default:
      return 'auth_requise'
  }
}

// Bail d'une collecte prise par un ordinateur

/** Une collecte prise est réservée à son ordinateur ce temps-là, renouvelé par ses battements. */
export const DUREE_BAIL_SECONDES = 120
export const BATTEMENT_AGENT_SECONDES = 30

export function bailExpireLe(maintenant: Date): Date {
  return new Date(maintenant.getTime() + DUREE_BAIL_SECONDES * 1000)
}

export function bailValide(expireLe: Date | null, maintenant: Date): boolean {
  return expireLe !== null && maintenant.getTime() < expireLe.getTime()
}

// Droits d'un ordinateur

/**
 * Un ordinateur ne prend que les collectes des connexions actives liées à lui, dont sa personne
 * est titulaire et qu'elle voit encore (`filtreParEntite`) ; révoqué, plus rien.
 */
export function agentPeutPrendre(
  agent: { id: string; personneId: string; revoque: boolean },
  connexion: { agentId: string | null; titulaireId: string; active: boolean },
  o: { visible: boolean },
): boolean {
  return (
    !agent.revoque &&
    o.visible &&
    connexion.active &&
    connexion.agentId === agent.id &&
    connexion.titulaireId === agent.personneId
  )
}

/**
 * Lier une connexion à un ordinateur (ou l'en délier) : son titulaire, dans l'appli, vers un de
 * ses ordinateurs (décision du 03/10/2026). La session du portail vivra sur cet ordinateur.
 */
export function erreurLiaison(e: {
  acteurId: string
  viaAssistant: boolean
  /**
   * En créant une connexion sans secret, l'assistant peut la lier à un ordinateur de son
   * titulaire (décision de Pierre-Marie du 08/10/2026) ; ensuite, dans l'appli seulement.
   */
  aLaCreation?: boolean
  connexion: { titulaireId: string; fournisseur: string }
  agent: { personneId: string; revoque: boolean } | null
}): string | null {
  if (e.viaAssistant && !e.aLaCreation)
    return 'Une connexion se lie à un ordinateur dans l’appli, jamais via l’assistant.'
  if (e.acteurId !== e.connexion.titulaireId)
    return 'Seul le titulaire de la connexion la lie à un ordinateur.'
  if (e.agent && e.agent.personneId !== e.connexion.titulaireId)
    return 'Cet ordinateur appartient à une autre personne.'
  if (e.agent?.revoque) return 'Cet ordinateur est révoqué.'
  if (!e.agent && compteSensible(e.connexion.fournisseur))
    return 'Compte sensible : jamais relevé par le serveur, seulement par un ordinateur de son titulaire.'
  return null
}

// Qui relève une connexion

/**
 * `a_relier` : aucun ordinateur, et le serveur ne la relève pas ; `a_reattribuer` : son ordinateur
 * est révoqué. Dans ces deux cas, rien ne la relève tant que son titulaire ne la relie pas.
 */
export type ReleveConnexion = 'serveur' | 'ordinateur' | 'a_relier' | 'a_reattribuer'

/** Le collecteur du serveur relève ce fournisseur : un adaptateur, et pas un compte sensible. */
export function serveurPeutRelever(fournisseur: string): boolean {
  return collecteParRobot(fournisseur) && !compteSensible(fournisseur)
}

/**
 * Qui relève une connexion, quel que soit le fournisseur : l'ordinateur lié tant qu'il n'est pas
 * révoqué ; sans ordinateur, le serveur là où il le peut. Un compte sensible (CLAUDE.md, « Collecte
 * locale sur un compte sensible ») ou un portail sans adaptateur (dépôt de fichiers) attend un
 * ordinateur de son titulaire.
 */
export function releveConnexion(c: {
  fournisseur: string
  agent: { revoque: boolean } | null
}): ReleveConnexion {
  if (c.agent) return c.agent.revoque ? 'a_reattribuer' : 'ordinateur'
  return serveurPeutRelever(c.fournisseur) ? 'serveur' : 'a_relier'
}

/** Pourquoi rien ne relève la connexion, et qui doit agir ; relevée : null. */
export function motifSansReleve(
  releve: ReleveConnexion,
  o: { titulaire?: string | null; ordinateur?: string | null },
): string | null {
  const qui = o.titulaire ?? 'son titulaire'
  const ou = 'dans Réglages, onglet « Connexions », « Portails », « Relever sur »'
  switch (releve) {
    case 'a_relier':
      return `Aucun ordinateur ne relève cette connexion : ${qui} la relie à un de ses ordinateurs, ${ou}.`
    case 'a_reattribuer':
      return `${o.ordinateur ?? 'Son ordinateur'} est révoqué : ${qui} relie cette connexion à un autre de ses ordinateurs, ${ou}.`
    default:
      return null
  }
}

// Appairage

/** Sans 0, O, 1, I ni L : un code se lit et se recopie sans ambiguïté. */
export const ALPHABET_APPAIRAGE = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
export const LONGUEUR_CODE_APPAIRAGE = 8
export const MINUTES_CODE_APPAIRAGE = 10

/** Code saisi sur l'ordinateur : espaces et tirets retirés, en majuscules ; sinon null. */
export function codeAppairageValide(saisie: string): string | null {
  const code = saisie.replace(/[\s-]/g, '').toUpperCase()
  if (code.length !== LONGUEUR_CODE_APPAIRAGE) return null
  return [...code].every((c) => ALPHABET_APPAIRAGE.includes(c)) ? code : null
}

/** Premier contact d'un ordinateur : macOS seulement (décision du 03/10/2026). */
export const appairageAgent = z
  .object({
    code: z
      .string()
      .max(20)
      .refine((c) => codeAppairageValide(c) !== null, 'Code illisible.'),
    libelle: z.string().trim().min(1).max(80),
    plateforme: z.enum(['macos']),
    version: z.string().regex(/^\d{1,3}\.\d{1,3}\.\d{1,4}$/),
  })
  .strict()
export type AppairageAgent = z.infer<typeof appairageAgent>

/**
 * Installation depuis l'écran « Ajouter cet ordinateur » : le code seul, envoyé en formulaire par
 * la ligne affichée (`curl -d code=…`). Il n'est pas pris : l'appairage qui suit le prend.
 */
export const installationAgent = z
  .object({
    code: z
      .string()
      .max(20)
      .refine((c) => codeAppairageValide(c) !== null, 'Code illisible.'),
  })
  .strict()

// Ce qu'un ordinateur rend compte

/** Événements qu'un ordinateur peut noter au journal d'une collecte ; rien d'autre. */
export const ACTIONS_AGENT = [
  'session_reutilisee',
  'session_illisible',
  'authentification_requise',
  'authentification_reussie',
  'fenetre_ouverte',
  'fenetre_fermee',
  'onglet_rendu',
  'onglet_ferme',
  'session_non_reconnue',
  'defi_resolu',
  'documents_trouves',
  'documents_choisis',
  'document_telecharge',
  'document_refuse',
  'requetes_coupees',
  // Échec : la page où la collecte s'est arrêtée (hôte et premier segment, masqué).
  'page_en_echec',
  'telechargement_manuel',
  // Portail de justificatifs : des nombres (lues, illisibles, lots) et un code de refus.
  'operations_lues',
  'dossier_refuse',
  // Étape d'écriture : l'arrêt de toutes les écritures d'un passage et son motif
  // (MOTIFS_ARRET_ECRITURES) ; chaque dépôt et chaque liaison ont leurs propres routes.
  'ecritures_arretees',
  // Activité pas encore facturée (Ulys : trajets en cours) : lue ou non, combien de mois, toute.
  'activite_lue',
] as const

const valeurDetail = z.union([z.string().max(120), z.number().finite(), z.boolean()])

export const evenementAgent = z
  .object({
    action: z.enum(ACTIONS_AGENT),
    details: z
      .record(z.string().regex(/^[a-zA-Z]{1,40}$/), valeurDetail)
      .refine((d) => Object.keys(d).length <= 8, 'Huit détails au plus.')
      .default({}),
  })
  .strict()
export type EvenementAgent = z.infer<typeof evenementAgent>

/**
 * Activité lue chez le fournisseur et pas encore facturée (Ulys : trajets « en cours ») : les mois
 * où elle se trouve, et si la lecture l'a vue toute ; en partie, aucun mois sans activité ne s'en
 * conclut.
 */
export const activitePortail = z
  .object({ mois: z.array(mois).max(600), complete: z.boolean() })
  .strict()
export type ActivitePortail = z.infer<typeof activitePortail>

/** Fin d'une collecte rendue par un ordinateur ; l'erreur est caviardée par le serveur aussi. */
export const finCollecteAgent = z
  .object({
    issue: z.enum(['terminer', 'echouer', 'bloquer']),
    classeErreur: z.enum(CLASSES_ERREUR_COLLECTE).optional(),
    documentsTrouves: z.int().min(0).max(10_000),
    documentsNouveaux: z.int().min(0).max(10_000),
    /** Documents laissés au-delà de la limite d'une collecte : la suivante vient vite. */
    restants: z.int().min(0).max(10_000).optional(),
    /** Mois de la liste du portail (`moisListes`) : les mois sans document s'en déduisent. */
    moisListes: z.array(mois).max(600).optional(),
    /** Mois des documents de la liste déjà relevés (`moisPresents`) : comptés relevés. */
    moisPresents: z.array(mois).max(600).optional(),
    /** Activité pas encore facturée (`ActivitePortail`) : mois à venir, mois sans document. */
    activite: activitePortail.optional(),
    erreur: z.string().max(500).optional(),
  })
  .strict()
  .refine((f) => f.documentsNouveaux <= f.documentsTrouves, {
    message: 'Plus de nouveaux que de trouvés.',
    path: ['documentsNouveaux'],
  })
export type FinCollecteAgent = z.infer<typeof finCollecteAgent>

// Requêtes d'un ordinateur

/** Les fournisseurs que l'ordinateur sait relever (clés de ses adaptateurs). */
export const priseAgent = z
  .object({ fournisseurs: z.array(z.string().regex(/^[a-z0-9_]{2,40}$/)).max(20) })
  .strict()

/** Un défi constaté par l'adaptateur ; le message est écrit dans l'adaptateur, jamais lu sur la page. */
export const ouvertureDefiAgent = z
  .object({
    type: z.enum(TYPES_DEFI),
    message: z.string().max(200).nullable(),
    enDirect: z.boolean(),
  })
  .strict()

/** Plus ancienne version de l'agent que le serveur accepte ; en dessous, il doit se mettre à jour. */
export const VERSION_MINIMALE_AGENT = '0.1.0'

/** `version` est au moins `minimale` (comparaison numérique, partie par partie). */
export function versionSuffisante(version: string, minimale = VERSION_MINIMALE_AGENT): boolean {
  const lire = (v: string) =>
    /^\d{1,3}\.\d{1,3}\.\d{1,4}$/.test(v) ? v.split('.').map(Number) : null
  const a = lire(version)
  const b = lire(minimale)
  if (!a || !b) return false
  for (let i = 0; i < 3; i++) if (a[i]! !== b[i]!) return a[i]! > b[i]!
  return true
}

// Lecture d'une ligne de liste (données, jamais consignes) : ce que le texte dit d'une date, d'un
// mois ou d'un montant, sans supposer la mise en page du portail. Rien de reconnu : rien de rendu
// (le document n'est alors filtré ni par période ni par montant).

const MOIS_FR = [
  'janvier',
  'fevrier',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'aout',
  'septembre',
  'octobre',
  'novembre',
  'decembre',
] as const

const sansAccents = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

/** Mois écrits en toutes lettres ou abrégés, en français et en anglais, sans accents. */
const MOIS_EN_LETTRES: Readonly<Record<string, number>> = {
  janvier: 1,
  janv: 1,
  january: 1,
  jan: 1,
  fevrier: 2,
  fevr: 2,
  fev: 2,
  february: 2,
  feb: 2,
  mars: 3,
  march: 3,
  mar: 3,
  avril: 4,
  avr: 4,
  april: 4,
  apr: 4,
  mai: 5,
  may: 5,
  juin: 6,
  june: 6,
  jun: 6,
  juillet: 7,
  juil: 7,
  july: 7,
  jul: 7,
  aout: 8,
  august: 8,
  aug: 8,
  septembre: 9,
  sept: 9,
  september: 9,
  sep: 9,
  octobre: 10,
  oct: 10,
  october: 10,
  novembre: 11,
  nov: 11,
  november: 11,
  decembre: 12,
  dec: 12,
  december: 12,
}

function jourSiValide(a: number, m: number, j: number): string | undefined {
  const date = `${a}-${String(m).padStart(2, '0')}-${String(j).padStart(2, '0')}`
  const v = new Date(`${date}T00:00:00Z`)
  return !Number.isNaN(v.getTime()) && v.toISOString().slice(0, 10) === date ? date : undefined
}

/**
 * Une date seule, écrite en lettres à la française (« 3 oct. 2026 », « 1er février 2026 ») ou à
 * l'anglaise (« September 30, 2026 »), ou en chiffres (« 30/09/2026 », « 2026-09-30 ») ; une date
 * qui n'existe pas, ou un autre texte : rien.
 */
export function jourEnLettres(texte: string): string | undefined {
  const t = sansAccents(texte)
    .replace(/[\u00a0\u202f]/g, ' ')
    .trim()
  let m = /^([a-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(t)
  if (m && MOIS_EN_LETTRES[m[1]!])
    return jourSiValide(Number(m[3]), MOIS_EN_LETTRES[m[1]!]!, Number(m[2]))
  m = /^(\d{1,2})(?:er)?\s+([a-z]+)\.?\s+(\d{4})$/.exec(t)
  if (m && MOIS_EN_LETTRES[m[2]!])
    return jourSiValide(Number(m[3]), MOIS_EN_LETTRES[m[2]!]!, Number(m[1]))
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t)
  if (m) return jourSiValide(Number(m[3]), Number(m[2]), Number(m[1]))
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t)
  if (m) return jourSiValide(Number(m[1]), Number(m[2]), Number(m[3]))
  return undefined
}

/** Date jj/mm/aaaa (la première) et mois couvert (« septembre 2026 », sinon celui de la date). */
export function periodeDansTexte(texte: string): { date?: string; periode?: string } {
  const t = sansAccents(texte)
  const r: { date?: string; periode?: string } = {}
  const d = /\b(\d{2})\/(\d{2})\/(\d{4})\b/.exec(t)
  if (d) {
    const jour = `${d[3]}-${d[2]}-${d[1]}`
    const v = new Date(`${jour}T00:00:00Z`)
    if (!Number.isNaN(v.getTime()) && v.toISOString().slice(0, 10) === jour) r.date = jour
  }
  const m = new RegExp(`\\b(${MOIS_FR.join('|')})\\s+(\\d{4})\\b`).exec(t)
  if (m) {
    const n = MOIS_FR.indexOf(m[1] as (typeof MOIS_FR)[number]) + 1
    r.periode = `${m[2]}-${String(n).padStart(2, '0')}`
  } else if (r.date) r.periode = r.date.slice(0, 7)
  return r
}

/**
 * Premier montant en euros écrit à la française (« 1 234,50 € », espaces ordinaires, insécables ou
 * fines), en centimes.
 */
export function montantDansTexte(texte: string): number | undefined {
  const m = /(-?)(\d{1,3}(?:\s\d{3})+|\d+),(\d{2})\s*€/.exec(texte)
  if (!m) return undefined
  const euros = Number(m[2]!.replace(/\s/g, ''))
  const centimes = euros * 100 + Number(m[3])
  return m[1] ? -centimes : centimes
}

// Besoins documentaires (PR8)

const PAS_CADENCE: Partial<Record<CadenceConnexion, number>> = {
  mensuelle: 1,
  bimestrielle: 2,
  trimestrielle: 3,
  semestrielle: 6,
  annuelle: 12,
}
/** Les mois manquants se cherchent sur un an au plus. */
export const HORIZON_MOIS_MANQUANTS = 12

const indexMois = (m: string) => Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7)) - 1
const moisDIndex = (i: number) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`

/**
 * Jour du mois suivant à partir duquel l'activité d'un mois est toute visible chez le fournisseur
 * (Ulys : trajets affichés sous trois à cinq jours, relevé le 02/11/2026).
 */
export const JOUR_ACTIVITE_VISIBLE = 8

/**
 * Mois où la cadence de la connexion attend un document qui n'a pas été relevé. La grille part
 * du dernier mois relevé (un fournisseur trimestriel garde ses mois) ; un relevé décalé de moins
 * d'une demi-période compte. Jusqu'au mois dernier (le mois en cours n'est pas encore publié),
 * pas avant le premier relevé ni plus d'un an en arrière. Sans cadence connue ni premier relevé,
 * rien n'est supposé : la collecte prend alors tout ce qui est nouveau.
 */
export function moisManquants(o: {
  cadence: CadenceConnexion
  /** Mois (AAAA-MM) des documents déjà relevés pour la connexion. */
  releves: readonly string[]
  /** Mois constatés sans document chez le fournisseur (`moisSansDocument`) : jamais attendus. */
  sansDocument?: readonly string[]
  /** Mois où la liste du portail montre un document déjà relevé (`moisPresents`) : relevés. */
  presents?: readonly string[]
  /** Jour civil à Paris (AAAA-MM-JJ). */
  aujourdhui: string
}): string[] {
  const pas = PAS_CADENCE[o.cadence]
  if (!pas) return []
  const fin = indexMois(o.aujourdhui) - 1
  const releves = [...o.releves, ...(o.presents ?? [])]
    .filter((m) => mois.safeParse(m).success)
    .map(indexMois)
    .filter((i) => i <= fin)
  if (releves.length === 0) return []
  const ancre = Math.max(...releves)
  const debut = Math.max(fin - HORIZON_MOIS_MANQUANTS + 1, Math.min(...releves))
  const tolerance = Math.floor((pas - 1) / 2)
  const manquants: number[] = []
  for (let p = ancre - Math.ceil((ancre - debut) / pas) * pas; p <= fin; p += pas) {
    if (p < debut) continue
    if (!releves.some((r) => Math.abs(r - p) <= tolerance)) manquants.push(p)
  }
  const vides = new Set(o.sansDocument ?? [])
  return manquants.map(moisDIndex).filter((m) => !vides.has(m))
}

/**
 * Les mois manquants (`moisManquants`), ceux dont l'activité attend encore sa facture
 * (`moisAVenir`) mis à part : elle n'est pas encore publiée, ils ne manquent pas encore.
 */
export function moisAttendus(
  o: Parameters<typeof moisManquants>[0] & { aVenir: readonly string[] },
): { manquants: string[]; aVenir: string[] } {
  const tous = moisManquants(o)
  const aVenir = new Set(o.aVenir)
  return {
    manquants: tous.filter((m) => !aVenir.has(m)),
    aVenir: tous.filter((m) => aVenir.has(m)),
  }
}

/**
 * Mois de l'activité pas encore facturée (`ActivitePortail`) dont la facture est à venir : jusqu'au
 * mois dernier (le mois en cours n'est jamais facturé), un an au plus, triés.
 */
export function moisAVenir(o: {
  activite: Pick<ActivitePortail, 'mois'> | null
  /** Jour civil à Paris (AAAA-MM-JJ). */
  aujourdhui: string
}): string[] {
  const fin = indexMois(o.aujourdhui) - 1
  const debut = fin - HORIZON_MOIS_MANQUANTS + 1
  const tous = new Set(
    (o.activite?.mois ?? [])
      .filter((m) => mois.safeParse(m).success)
      .map(indexMois)
      .filter((i) => i >= debut && i <= fin),
  )
  return [...tous].sort((a, b) => a - b).map(moisDIndex)
}

/**
 * Mois des documents d'une liste du portail (le mois donné, sinon celui de la date), triés. Un
 * document sans mois lisible, ou une liste vide : null, rien ne s'en conclut.
 */
export function moisListes(documents: readonly DocumentDisponible[]): string[] | null {
  const vus = new Set<string>()
  for (const d of documents) {
    const m = d.periode ?? d.date?.slice(0, 7)
    if (!m || !mois.safeParse(m).success) return null
    vus.add(m)
  }
  return vus.size > 0 ? [...vus].sort() : null
}

/**
 * Mois constatés sans document chez le fournisseur : ceux qu'une liste complète du portail
 * encadre (un document avant, un après) sans en montrer aucun ; et, l'activité pas encore
 * facturée lue toute, ceux d'après le dernier document qui n'en ont aucune (le fournisseur ne
 * facture pas un mois vide ; le mois dernier, à partir du `JOUR_ACTIVITE_VISIBLE`). Les constats
 * d'avant restent, sauf un mois où la liste montre désormais un document ; jusqu'au mois dernier,
 * un an au plus.
 */
export function moisSansDocument(o: {
  anciens: readonly string[]
  /** Mois de la liste du portail (`moisListes`) ; null : rien de nouveau n'est constaté. */
  listes: readonly string[] | null
  /** Activité pas encore facturée ; absente ou lue en partie : rien ne s'en conclut. */
  activite?: ActivitePortail | null
  /** Jour civil à Paris (AAAA-MM-JJ). */
  aujourdhui: string
}): string[] {
  const fin = indexMois(o.aujourdhui) - 1
  const debut = fin - HORIZON_MOIS_MANQUANTS + 1
  const listes = new Set((o.listes ?? []).filter((m) => mois.safeParse(m).success).map(indexMois))
  const vides = new Set(
    o.anciens
      .filter((m) => mois.safeParse(m).success)
      .map(indexMois)
      .filter((i) => !listes.has(i)),
  )
  if (listes.size > 0) {
    const tries = [...listes].sort((a, b) => a - b)
    for (let i = tries[0]! + 1; i < tries.at(-1)!; i++) if (!listes.has(i)) vides.add(i)
    if (o.activite?.complete) {
      const actifs = new Set(
        o.activite.mois.filter((m) => mois.safeParse(m).success).map(indexMois),
      )
      const visible = Number(o.aujourdhui.slice(8, 10)) >= JOUR_ACTIVITE_VISIBLE ? fin : fin - 1
      for (let i = tries.at(-1)! + 1; i <= visible; i++) if (!actifs.has(i)) vides.add(i)
    }
  }
  return [...vides]
    .filter((i) => i >= debut && i <= fin)
    .sort((a, b) => a - b)
    .map(moisDIndex)
}

/**
 * Mois où la liste du portail montre un document déjà relevé (référence connue ou déposée) :
 * le portail le date à sa création, la pièce à la date du document, et les deux mois peuvent
 * différer. Ajoutés aux constats d'avant ; jusqu'au mois dernier, un an au plus.
 */
export function moisPresents(o: {
  anciens: readonly string[]
  /** Mois de ces documents (`moisListes`) ; null : rien de nouveau n'est constaté. */
  presents: readonly string[] | null
  /** Jour civil à Paris (AAAA-MM-JJ). */
  aujourdhui: string
}): string[] {
  const fin = indexMois(o.aujourdhui) - 1
  const debut = fin - HORIZON_MOIS_MANQUANTS + 1
  const tous = new Set(
    [...o.anciens, ...(o.presents ?? [])]
      .filter((m) => mois.safeParse(m).success)
      .map(indexMois)
      .filter((i) => i >= debut && i <= fin),
  )
  return [...tous].sort((a, b) => a - b).map(moisDIndex)
}

/** Le besoin d'une collecte qui cherche ces mois ; aucun : null (besoin par défaut). */
export function besoinPourManquants(
  manquants: readonly string[],
  types: readonly TypeDocumentPortail[] = ['facture'],
): BesoinCollecte | null {
  if (manquants.length === 0) return null
  const tries = [...manquants].sort()
  return besoinCollecte.parse({
    types: [...types],
    periodeDebut: tries[0],
    periodeFin: tries.at(-1),
    maxDocuments: MAX_DOCUMENTS_COLLECTE,
  })
}
